import React from 'react';
import { BaseFilterPopover, FilterAutocompleteBox } from '../../Common/FilterPopover';
import { Divider, FormControlLabel, Switch } from '@mui/material';
import AuthContext from '../../Common/AuthContext';
import { AuthContextValue, OptionsInterface } from '../../Common/types';
import { addRegisterController, clearRegisterControllers, deleteRegisterController } from '../../User/usersApi';

type Props = {
  rcVaUsers: OptionsInterface[];
  loadingRcUsers: boolean;
  loadingOperationalManagers: boolean;
  registerControllers: OptionsInterface[];
  setRegisterControllers: (val: OptionsInterface[]) => void;
  loadingRegisterControllers: boolean;
  showNotHandled: boolean | undefined;
  setShowNotHandled: (val: boolean) => void;
  showRequiresCompletion: boolean | undefined;
  setShowRequiresCompletion: (val: boolean) => void;
  showUnderInvestigation: boolean | undefined;
  setShowUnderInvestigation: (val: boolean) => void;
  showApproved: boolean | undefined;
  setShowApproved: (val: boolean) => void;
  showExceptionValidPrevious: boolean | undefined;
  setShowExceptionValidPrevious: (val: boolean) => void;
  showExceptionUnderFourteen: boolean | undefined;
  setShowExceptionUnderFourteen: (val: boolean) => void;
  showExceptionNotRequired: boolean | undefined;
  setShowExceptionNotRequired: (val: boolean) => void;
  showExceptionMigration: boolean | undefined;
  setShowExceptionMigration: (val: boolean) => void;
  showComplete: boolean | undefined;
  setShowComplete: (val: boolean) => void;
};

export default function RegisterRequestsFilterPopover({
  rcVaUsers,
  loadingRcUsers,
  loadingOperationalManagers,
  registerControllers,
  setRegisterControllers,
  loadingRegisterControllers,
  showNotHandled,
  setShowNotHandled,
  showRequiresCompletion,
  setShowRequiresCompletion,
  showUnderInvestigation,
  setShowUnderInvestigation,
  showApproved,
  setShowApproved,
  showExceptionValidPrevious,
  setShowExceptionValidPrevious,
  showExceptionUnderFourteen,
  setShowExceptionUnderFourteen,
  showExceptionNotRequired,
  setShowExceptionNotRequired,
  showExceptionMigration,
  setShowExceptionMigration,
  showComplete,
  setShowComplete,
}: Props) {
  const { hasPermissions } = React.useContext(AuthContext) as AuthContextValue;

  return (
    <BaseFilterPopover>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Ej Hanterad
        <FormControlLabel
          label=""
          labelPlacement="start"
          control={<Switch checked={showNotHandled ?? true} onChange={(_, checked) => setShowNotHandled(checked)} />}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Komplettering
        <FormControlLabel
          label=""
          labelPlacement="start"
          control={
            <Switch
              checked={showRequiresCompletion ?? false}
              onChange={(_, checked) => setShowRequiresCompletion(checked)}
            />
          }
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Utredning
        <FormControlLabel
          label=""
          labelPlacement="start"
          control={
            <Switch
              checked={showUnderInvestigation ?? false}
              onChange={(_, checked) => setShowUnderInvestigation(checked)}
            />
          }
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Godkända
        <FormControlLabel
          label=""
          labelPlacement="start"
          control={<Switch checked={showApproved ?? false} onChange={(_, checked) => setShowApproved(checked)} />}
        />
      </div>

      {showApproved && (
        <div style={{ marginLeft: 24, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Undantagen (giltig RU)
            <Switch
              size="small"
              checked={showExceptionValidPrevious ?? true}
              onChange={(_, checked) => setShowExceptionValidPrevious(checked)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Undantagen (under 14)
            <Switch
              size="small"
              checked={showExceptionUnderFourteen ?? true}
              onChange={(_, checked) => setShowExceptionUnderFourteen(checked)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Undantagen (avtalspart)
            <Switch
              size="small"
              checked={showExceptionNotRequired ?? true}
              onChange={(_, checked) => setShowExceptionNotRequired(checked)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Undantagen (migration)
            <Switch
              size="small"
              checked={showExceptionMigration ?? true}
              onChange={(_, checked) => setShowExceptionMigration(checked)}
            />
          </div>
        </div>
      )}

      <Divider className="!my-[15px]" />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Begäran med uppladdade filer
        <FormControlLabel
          label=""
          labelPlacement="start"
          control={<Switch checked={showComplete ?? true} onChange={(_, checked) => setShowComplete(checked)} />}
        />
      </div>

      {hasPermissions(['admin', 'operational_leader']) && (
        <>
          <Divider className="!my-[15px]" />

          <div className="flex flex-col gap-2">
            Registerkontrollanter
            <FilterAutocompleteBox
              multiple
              options={rcVaUsers}
              value={registerControllers}
              onChange={async (_, options, reason, details) => {
                if (reason === 'clear') {
                  clearRegisterControllers().then(({ data }) => {
                    setRegisterControllers([]);
                  });
                }
                if (reason === 'selectOption') {
                  if (details?.option.id) {
                    addRegisterController(details.option.id.toString()).then(({ data }) => {
                      setRegisterControllers(data.data);
                    });
                  }
                }
                if (reason === 'removeOption') {
                  if (details?.option.id) {
                    deleteRegisterController(details.option.id.toString()).then(({ data }) => {
                      setRegisterControllers(data.data);
                    });
                  }
                }
              }}
              placeholder="Sök användare"
              getOptionLabel={(option) => option.name}
              clearable
              loading={loadingRcUsers || loadingOperationalManagers || loadingRegisterControllers}
              className="w-48 2xl:w-64"
            />
          </div>
        </>
      )}
    </BaseFilterPopover>
  );
}
